import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ticketsAPI } from '../services/api';
import { formatInTz } from '../utils/timezone';

const TIMER_STOP_STATUSES     = ['Completed', 'Voided', 'Closed', 'Resolved'];
const TIMER_PAUSE_STATUSES    = ['Pending Action', 'Pending Approval'];
const TIMER_NO_START_STATUSES = [...TIMER_STOP_STATUSES, 'Pending User Acceptance', 'Rejected'];

/** BIGINT from PostgreSQL may arrive as a string; legacy bugs could store concat-corrupted values. */
const MAX_SANE_ELAPSED_MS = 10 * 365 * 24 * 3600 * 1000; // 10 years

export function normalizeElapsedMs(val) {
  const n = Number(val);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > MAX_SANE_ELAPSED_MS) return 0;
  return n;
}

export function getLiveElapsedMs(baseMs, startedAt, at = Date.now()) {
  const base = normalizeElapsedMs(baseMs);
  if (!startedAt) return base;
  const start = new Date(startedAt).getTime();
  if (!Number.isFinite(start)) return base;
  return base + Math.max(0, at - start);
}

function formatMs(ms) {
  ms = normalizeElapsedMs(ms);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;
  return `${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;
}

// Exported helper so other components can display elapsed time
export function formatMsShort(ms) {
  ms = normalizeElapsedMs(ms);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${String(h).padStart(2,'0')}h ${String(m).padStart(2,'0')}m`;
}

export default function TicketTimer({ ticketId, initialTimerStatus, initialElapsedMs, initialStartedAt, ticketStatus, onTimerChange, userRole }) {
  const [timerStatus,  setTimerStatus]  = useState(initialTimerStatus || 'stopped');
  const [baseMs,       setBaseMs]       = useState(Number(initialElapsedMs) || 0);
  const [startedAt,    setStartedAt]    = useState(initialStartedAt ? new Date(initialStartedAt) : null);
  const [displayMs,    setDisplayMs]    = useState(Number(initialElapsedMs) || 0);
  const [saving,       setSaving]       = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseRemark,    setPauseRemark]    = useState('');
  const [pauseError,     setPauseError]     = useState('');
  const [latestReq,      setLatestReq]      = useState(null);
  const intervalRef      = useRef(null);
  const autoStartedRef   = useRef(false);
  // Keep a stable ref to the latest onTimerChange so applyTimerState never needs
  // onTimerChange in its dependency array (which would cause an infinite fetch loop
  // because onTimerChange is an inline function that changes on every parent render).
  const onTimerChangeRef = useRef(onTimerChange);
  useEffect(() => { onTimerChangeRef.current = onTimerChange; });

  const applyTimerState = useCallback((result) => {
    if (!result) return;
    const newBase      = Number(result.timer_elapsed_ms) || 0;
    const newStartedAt = result.timer_started_at ? new Date(result.timer_started_at) : null;
    setTimerStatus(result.timer_status || 'stopped');
    setBaseMs(newBase);
    setStartedAt(newStartedAt);
    onTimerChangeRef.current && onTimerChangeRef.current({
      timer_status: result.timer_status,
      timer_elapsed_ms: newBase,
      timer_started_at: result.timer_started_at,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always load fresh timer state when opening a ticket (list cache can be stale)
  useEffect(() => {
    if (!ticketId) return;
    ticketsAPI.get(ticketId)
      .then(t => applyTimerState(t))
      .catch(() => {});
  }, [ticketId, applyTimerState]);

  // Fetch latest pause-request info so requester can see manager's decision
  const refreshLatestReq = useCallback(() => {
    if (!ticketId) return;
    ticketsAPI.getLatestPauseRequest(ticketId)
      .then(setLatestReq)
      .catch(() => {});
  }, [ticketId]);

  useEffect(() => { refreshLatestReq(); }, [refreshLatestReq]);

  // Always keep a ref to the latest applyTimerState so polling intervals never use stale closures
  const applyTimerStateRef = useRef(applyTimerState);
  useEffect(() => { applyTimerStateRef.current = applyTimerState; });

  // While awaiting pause approval: poll both pause-request status AND ticket timer status
  // every 3 seconds. As soon as the server reports a status other than 'awaiting_pause'
  // (approved → 'paused', rejected → 'running'), apply it immediately to stop/resume the tick.
  useEffect(() => {
    if (timerStatus !== 'awaiting_pause' || !ticketId) return;
    const sync = async () => {
      const [req, t] = await Promise.all([
        ticketsAPI.getLatestPauseRequest(ticketId).catch(() => null),
        ticketsAPI.get(ticketId).catch(() => null),
      ]);
      if (req) setLatestReq(req);
      if (t && t.timer_status !== 'awaiting_pause') {
        applyTimerStateRef.current(t);
      }
    };
    sync();
    const id = setInterval(sync, 3000);
    return () => clearInterval(id);
  }, [timerStatus, ticketId]);

  // Universal safety-net poll while timer is running.
  // Covers the case where the frontend shows 'running' but the DB was already updated to
  // 'paused' (e.g. manager approved while the agent had a stale frontend state, or
  // the pause-request API call errored so timerStatus never transitioned to 'awaiting_pause').
  useEffect(() => {
    if (timerStatus !== 'running' || !ticketId) return;
    const sync = async () => {
      const [t, req] = await Promise.all([
        ticketsAPI.get(ticketId).catch(() => null),
        ticketsAPI.getLatestPauseRequest(ticketId).catch(() => null),
      ]);
      if (req !== null && req !== undefined) setLatestReq(req);
      if (t && t.timer_status !== 'running') {
        applyTimerStateRef.current(t);
      }
    };
    const id = setInterval(sync, 4000);
    return () => clearInterval(id);
  }, [timerStatus, ticketId]);

  // Instant update: when manager approves/rejects from Topbar or PauseRequestsAdmin
  // (same browser), a custom DOM event fires — respond immediately without waiting for poll.
  useEffect(() => {
    if (!ticketId) return;
    const onApproved = async () => {
      const [t, req] = await Promise.all([
        ticketsAPI.get(ticketId).catch(() => null),
        ticketsAPI.getLatestPauseRequest(ticketId).catch(() => null),
      ]);
      if (req !== null && req !== undefined) setLatestReq(req);
      if (t) applyTimerStateRef.current(t);
    };
    const onRejected = onApproved; // same refresh for rejection
    window.addEventListener('sysnet-pause-approved', onApproved);
    window.addEventListener('sysnet-pause-rejected', onRejected);
    return () => {
      window.removeEventListener('sysnet-pause-approved', onApproved);
      window.removeEventListener('sysnet-pause-rejected', onRejected);
    };
  }, [ticketId]);

  // Tick display every second when live
  useEffect(() => {
    clearInterval(intervalRef.current);
    const isLive = (timerStatus === 'running' || timerStatus === 'awaiting_pause') && startedAt;
    if (isLive) {
      const tick = () => setDisplayMs(getLiveElapsedMs(baseMs, startedAt));
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setDisplayMs(baseMs);
    }
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerStatus, startedAt, baseMs]);

  // Auto-start ONLY for brand-new tickets (no elapsed time yet)
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (TIMER_NO_START_STATUSES.includes(ticketStatus)) return;
    if (TIMER_PAUSE_STATUSES.includes(ticketStatus)) return;
    const elapsedZero = !initialElapsedMs || Number(initialElapsedMs) === 0;
    if (timerStatus === 'stopped' && elapsedZero) {
      autoStartedRef.current = true;
      callTimerAction('start');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to ticket status changes
  const prevStatusRef = useRef(ticketStatus);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (prev === ticketStatus) return;
    prevStatusRef.current = ticketStatus;
    if (TIMER_STOP_STATUSES.includes(ticketStatus)) {
      if (timerStatus === 'running' || timerStatus === 'awaiting_pause') callTimerAction('stop');
    } else if (TIMER_PAUSE_STATUSES.includes(ticketStatus)) {
      if (timerStatus === 'running') callTimerAction('pause');
    } else if (!TIMER_NO_START_STATUSES.includes(ticketStatus)) {
      // Only auto-resume if the PREVIOUS status was a pause-triggering status.
      // Prevents page-load (undefined → 'Open') from auto-resuming a manually-paused timer.
      if (timerStatus === 'paused' && TIMER_PAUSE_STATUSES.includes(prev)) {
        callTimerAction('resume');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketStatus]);

  // When timer transitions out of awaiting_pause (approved/rejected/cancelled),
  // immediately refresh the pause request so the banner updates without waiting for next poll.
  const prevTimerStatusRef = useRef(timerStatus);
  useEffect(() => {
    const prev = prevTimerStatusRef.current;
    prevTimerStatusRef.current = timerStatus;
    if (prev === 'awaiting_pause' && timerStatus !== 'awaiting_pause') {
      refreshLatestReq();
    }
  }, [timerStatus, refreshLatestReq]);

  const callTimerAction = useCallback(async (action, remark) => {
    setSaving(true);
    // Clear stale approved/rejected banner immediately when resuming so it
    // doesn't re-trigger the extra-guard polling loop above.
    if (action === 'resume') setLatestReq(null);
    try {
      const result = await ticketsAPI.timerAction(ticketId, action, remark);
      applyTimerState(result);
      refreshLatestReq();
    } catch(e) {
      console.error('Timer action failed', e);
    } finally {
      setSaving(false);
    }
  }, [ticketId, applyTimerState, refreshLatestReq]);

  const handlePauseSubmit = useCallback(async () => {
    if (!pauseRemark.trim()) { setPauseError('Remark is required.'); return; }
    setPauseError('');
    setShowPauseModal(false);
    await callTimerAction('pause-request', pauseRemark.trim());
    setPauseRemark('');
  }, [pauseRemark, callTimerAction]);

  const handleCancelPauseRequest = useCallback(async () => {
    await callTimerAction('cancel-pause-request');
  }, [callTimerAction]);

  const isManager = ['admin','manager','ceo','supervisor','lead'].includes(userRole);
  const isRunning       = timerStatus === 'running';
  const isPaused        = timerStatus === 'paused';
  const isStopped       = timerStatus === 'stopped';
  const isAwaitingPause = timerStatus === 'awaiting_pause';
  const isClosed        = TIMER_STOP_STATUSES.includes(ticketStatus);

  if (isClosed && isStopped) return null;
  const barBg     = isRunning || isAwaitingPause ? 'var(--accent-bg)' : isPaused ? '#FFFBEB' : '#F8FAFC';
  const barBorder = isRunning || isAwaitingPause ? 'var(--accent-light)' : isPaused ? '#FDE68A' : '#E2E8F0';
  const timeColor = isRunning || isAwaitingPause ? 'var(--accent)' : '#1A202C';

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', marginBottom: 14,
        background: barBg, borderRadius: 10,
        border: `1.5px solid ${barBorder}`,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>⏱</span>

        <span style={{ fontWeight: 800, fontSize: 15, color: timeColor, fontFamily: 'monospace', minWidth: 90 }}>
          {formatMs(displayMs)}
        </span>

        {/* Status pill */}
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, whiteSpace: 'nowrap',
          background: isRunning       ? 'var(--accent)'
                    : isAwaitingPause ? '#F59E0B'
                    : isPaused        ? '#FDE68A'
                    : '#E2E8F0',
          color:      isRunning       ? '#fff'
                    : isAwaitingPause ? '#fff'
                    : isPaused        ? '#92400E'
                    : '#64748B',
        }}>
          {isRunning ? '● Running'
            : isAwaitingPause ? '⏳ Pause Pending Approval'
            : isPaused ? '⏸ Paused'
            : '⏹ Stopped'}
        </span>

        {/* Action buttons — Cancel Request lives in the banner below, not here */}
        {!saving && (
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {isRunning && !TIMER_PAUSE_STATUSES.includes(ticketStatus) && (
              <button onClick={() => { setPauseRemark(''); setPauseError(''); setShowPauseModal(true); }} style={btnStyle('#F59E0B')}>
                ⏸ Pause
              </button>
            )}
            {isPaused && !TIMER_PAUSE_STATUSES.includes(ticketStatus) && !isClosed && (
              <button onClick={() => callTimerAction('resume')} style={btnStyle('var(--accent)')}>
                ▶ Resume
              </button>
            )}
            {isStopped && !TIMER_NO_START_STATUSES.includes(ticketStatus) && !TIMER_PAUSE_STATUSES.includes(ticketStatus) && (
              <button onClick={() => callTimerAction('start')} style={btnStyle('var(--accent)')}>
                ▶ Start
              </button>
            )}
          </div>
        )}
        {saving && <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 'auto' }}>Saving…</span>}
      </div>

      {/* Pause-request status banner (visible to requester + manager)
           - "pending" only shown while timer is actually awaiting approval (not stale after approval)
           - "Cancel Request" button lives here (not in the header) */}
      {latestReq && (
        (latestReq.status === 'pending' && isAwaitingPause) ||
        (latestReq.status === 'approved' && isPaused) ||
        latestReq.status === 'rejected'
      ) && (
        <div style={{
          margin: '-8px 0 14px',
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 12,
          display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap',
          background: latestReq.status === 'approved' ? '#ECFDF5'
                    : latestReq.status === 'rejected' ? '#FEF2F2'
                    : '#FFFBEB',
          border: `1px solid ${
            latestReq.status === 'approved' ? '#A7F3D0'
            : latestReq.status === 'rejected' ? '#FECACA'
            : '#FDE68A'
          }`,
          color: latestReq.status === 'approved' ? '#065F46'
               : latestReq.status === 'rejected' ? '#991B1B'
               : '#92400E',
        }}>
          <span style={{ fontSize: 14 }}>
            {latestReq.status === 'approved' ? '✅' : latestReq.status === 'rejected' ? '❌' : '⏳'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700 }}>
              {latestReq.status === 'pending' && <>Pause request pending — sent to manager by <b>{latestReq.requested_by_name}</b></>}
              {latestReq.status === 'approved' && <>Pause approved by <b>{latestReq.reviewed_by_name || 'Manager'}</b>{latestReq.reviewed_at ? ` • ${formatInTz(latestReq.reviewed_at, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}</>}
              {latestReq.status === 'rejected' && <>Pause rejected by <b>{latestReq.reviewed_by_name || 'Manager'}</b>{latestReq.reviewed_at ? ` • ${formatInTz(latestReq.reviewed_at, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}</>}
            </div>
            {latestReq.remark && (
              <div style={{ marginTop: 3, opacity: 0.9 }}>
                <span style={{ fontWeight: 600 }}>Reason:</span> {latestReq.remark}
              </div>
            )}
            {latestReq.review_note && (
              <div style={{ marginTop: 2, opacity: 0.9 }}>
                <span style={{ fontWeight: 600 }}>Manager note:</span> {latestReq.review_note}
              </div>
            )}
          </div>
          {/* Cancel Request button inside the banner — only when still pending */}
          {latestReq.status === 'pending' && isAwaitingPause && !saving && (
            <button
              onClick={handleCancelPauseRequest}
              style={{
                alignSelf: 'center', flexShrink: 0,
                padding: '5px 12px', fontSize: 11, fontWeight: 700,
                borderRadius: 6, border: 'none', background: '#94A3B8',
                color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              ✕ Cancel Request
            </button>
          )}
        </div>
      )}

      {/* Pause Remark Modal */}
      {showPauseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: 440, maxWidth: '95vw', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1A202C', marginBottom: 6 }}>Request Timer Pause</div>
            <div style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>
              Your manager must approve before the timer is paused. Please provide a reason.
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
              Reason / Remark <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              autoFocus
              value={pauseRemark}
              onChange={e => { setPauseRemark(e.target.value); setPauseError(''); }}
              rows={4}
              placeholder="e.g. Waiting for customer callback, escalating to vendor…"
              style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${pauseError ? '#ef4444' : '#E2E8F0'}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
            />
            {pauseError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{pauseError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowPauseModal(false)} style={{ padding: '9px 20px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handlePauseSubmit} style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: '#F59E0B', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                Send to Manager
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function btnStyle(bg) {
  return {
    padding: '5px 12px', fontSize: 11, fontWeight: 700,
    borderRadius: 6, border: 'none', background: bg,
    color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
  };
}
